import { DeleteOutlined, StarFilled, StarOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Alert,
  App,
  Button,
  Card,
  Empty,
  Image,
  Input,
  Popconfirm,
  Space,
  Tag,
  Typography,
  Upload,
} from 'antd';
import { useState } from 'react';
import { api, ApiRequestError, errorText } from '../../api/client';
import type { ProductDetail } from '../../api/types';

interface Media {
  id: string;
}
interface Presign {
  upload_url: string;
  headers: Record<string, string>;
  storage_key: string;
}

/**
 * Ảnh sản phẩm. Hai cách:
 *  1. Tải ảnh từ máy → PUT thẳng lên R2 bằng URL ký sẵn, API kiểm tra magic bytes rồi mới nhận.
 *  2. Dán URL ảnh đã có trên website Navicosa (tên miền được phép).
 */
export function ImagesTab({
  product,
  canEdit,
  onSaved,
}: {
  product: ProductDetail;
  canEdit: boolean;
  onSaved: (p: ProductDetail) => void;
}) {
  const { message } = App.useApp();
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [storageOff, setStorageOff] = useState(false);

  const attach = async (mediaId: string) =>
    onSaved(
      await api.post<ProductDetail>(`/admin/products/${product.id}/images`, {
        media_file_id: mediaId,
      }),
    );

  const addByUrl = async () => {
    setBusy(true);
    try {
      const m = await api.post<Media>('/admin/media', { url: url.trim(), alt_text: product.name });
      await attach(m.id);
      setUrl('');
      message.success('Đã thêm ảnh');
    } catch (e) {
      message.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const ps = await api.post<Presign>('/admin/media/presign', {
        filename: file.name,
        mime_type: file.type,
        size: file.size,
      });
      const put = await fetch(ps.upload_url, { method: 'PUT', headers: ps.headers, body: file });
      if (!put.ok) throw new Error('Tải ảnh lên kho lưu trữ thất bại');
      const m = await api.post<Media>('/admin/media', {
        storage_key: ps.storage_key,
        alt_text: product.name,
      });
      await attach(m.id);
      message.success('Đã tải ảnh');
    } catch (e) {
      if (e instanceof ApiRequestError && e.error.code === 'STORAGE_NOT_CONFIGURED')
        setStorageOff(true);
      message.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn: () => Promise<ProductDetail>, ok: string) => {
    try {
      onSaved(await fn());
      message.success(ok);
    } catch (e) {
      message.error(errorText(e));
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {canEdit && (
        <Space wrap>
          <Upload
            accept="image/jpeg,image/png,image/webp,image/avif"
            showUploadList={false}
            beforeUpload={(f) => (void upload(f), false)}
          >
            <Button icon={<UploadOutlined />} loading={busy}>
              Tải ảnh từ máy
            </Button>
          </Upload>
          <Input
            style={{ width: 420 }}
            placeholder="hoặc dán URL ảnh trên xenangnavicosa.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <Button onClick={() => void addByUrl()} disabled={!url.trim()} loading={busy}>
            Thêm bằng URL
          </Button>
        </Space>
      )}
      {storageOff && (
        <Alert
          type="warning"
          showIcon
          message="Kho lưu ảnh (Cloudflare R2) chưa được cấu hình trên máy chủ này. Tạm thời dùng ảnh có sẵn bằng URL."
        />
      )}
      <Typography.Text type="secondary">
        Ưu tiên ảnh xe thật, rõ model, không bị cắt thiết bị. Không dùng ảnh AI làm ảnh sản phẩm
        (PRD 4.6).
      </Typography.Text>
      {product.images.length === 0 ? (
        <Empty description="Chưa có ảnh – Mini App sẽ hiện ảnh giữ chỗ" />
      ) : (
        <Space wrap size="middle">
          {product.images.map((img) => (
            <Card
              key={img.id}
              size="small"
              style={{ width: 220 }}
              cover={
                <Image
                  src={img.file_url}
                  height={200}
                  style={{ objectFit: 'contain', background: '#fff' }}
                />
              }
              actions={
                canEdit
                  ? [
                      img.is_primary ? (
                        <StarFilled key="p" style={{ color: '#D97706' }} />
                      ) : (
                        <StarOutlined
                          key="p"
                          title="Đặt làm ảnh chính"
                          onClick={() =>
                            void act(
                              () =>
                                api.patch(`/admin/products/${product.id}/images/${img.id}`, {
                                  is_primary: true,
                                }),
                              'Đã đặt ảnh chính',
                            )
                          }
                        />
                      ),
                      <Popconfirm
                        key="d"
                        title="Xóa ảnh này?"
                        onConfirm={() =>
                          void act(
                            () => api.del(`/admin/products/${product.id}/images/${img.id}`),
                            'Đã xóa ảnh',
                          )
                        }
                      >
                        <DeleteOutlined />
                      </Popconfirm>,
                    ]
                  : undefined
              }
            >
              {img.is_primary && <Tag color="orange">Ảnh chính</Tag>}
              <Typography.Text ellipsis style={{ fontSize: 12 }}>
                {img.alt_text}
              </Typography.Text>
            </Card>
          ))}
        </Space>
      )}
    </Space>
  );
}
